import { Navigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import type { Role } from '../../types'

interface Props {
  children: React.ReactNode
  requiredRole?: Role
}

export function ProtectedRoute({ children, requiredRole }: Props) {
  const { session, profile, loading, signOut } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-content">
        <div className="text-brand text-lg">Loading...</div>
      </div>
    )
  }

  if (!session) {
    return <Navigate to="/login" replace />
  }

  // User is authenticated but has no org memberships — awaiting approval
  if (profile && profile.memberships.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-content">
        <div className="w-full max-w-sm text-center">
          <img src="/redvision-logo-2x.png" alt="Redvision" className="h-10 mx-auto mb-6" />
          <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6 space-y-4">
            <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center mx-auto">
              <span className="text-brand text-xl">&#x23F3;</span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Awaiting Approval</h2>
            <p className="text-sm text-gray-500">
              Your account has been created but hasn't been assigned to an organization yet.
              Please contact your administrator to get access.
            </p>
            <p className="text-xs text-gray-400">{profile.email}</p>
            <button
              onClick={() => signOut()}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 rounded-md transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (requiredRole === 'rv_admin' && profile?.role !== 'rv_admin') {
    return <Navigate to="/" replace />
  }

  if (requiredRole === 'org_admin' && (profile?.role === 'viewer' || profile?.role === 'export')) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
